<template>
  <div class="stats-display" :class="[{ collapsed: isCollapsed }, { fullscreen }, { 'summary-mode': summaryMode }, { inline }, { 'sidebar-open': !sidebarCollapsed, 'sidebar-collapsed': sidebarCollapsed }]">
    <div v-if="!summaryMode && !inline" class="stats-header">
      <div class="stats-tabs">
        <button
          v-for="tab in tabs"
          :key="tab"
          class="btn tab-btn"
          :class="{ active: activeTab === tab }"
          @click="activeTab = tab"
        >
          {{ tab }}
        </button>
      </div>
      <div v-if="!inline" class="header-actions">
        <button class="btn collapse-btn" @click="toggleCollapsed">
          {{ isCollapsed ? 'Expand' : 'Collapse' }}
        </button>
        <button class="btn fullscreen-btn" @click="toggleFullscreen">
          {{ fullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}
        </button>
      </div>
    </div>
    <div v-show="!isCollapsed" class="stats-content">
      <section class="stats-section" v-if="!inline && (summaryMode || currentTab === 'All')">
        <div class="match-stats-grid" :class="{ breakdown: showSetBreakdown }">
            <div class="match-stats-header">{{ teamName }}</div>
            <div class="match-stats-header legend"></div>
            <div class="match-stats-header">{{ oppositionName }}</div>

          <div class="match-stats-group">
            <div class="match-stats-value" :class="{ 'msv-win': teamWins(displayedTeamScore, displayedOppScore) }">
              <span class="msv-total">
                <template v-if="activeSetId === 'total'">{{ cumulativeTeamPoints }}</template>
                <template v-else>{{ currentSetTeamScore !== null ? currentSetTeamScore : teamSetPointsString }}</template>
              </span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('score')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
            <div class="match-stats-label">
              <template v-if="activeSetId === 'total'">Total points</template>
              <template v-else>Set score</template>
            </div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(displayedTeamScore, displayedOppScore) }">
              <span class="msv-total">
                <template v-if="activeSetId === 'total'">{{ cumulativeOppositionPoints }}</template>
                <template v-else>{{ currentSetOppositionScore !== null ? currentSetOppositionScore : oppositionSetPointsString }}</template>
              </span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('score')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
          </div>
          <div class="match-stats-spacer"></div>

          <div class="match-stats-group">
            <div class="match-stats-value" :class="{ 'msv-win': teamWins(matchStats.team.pointsWon, matchStats.opposition.pointsWon) }">
              <span class="msv-total">{{ matchStats.team.pointsWon }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('pointsWon')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
            <div class="match-stats-label">Points scored outright</div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(matchStats.team.pointsWon, matchStats.opposition.pointsWon) }">
              <span class="msv-total">{{ matchStats.opposition.pointsWon }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('pointsWon')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>

            <div class="match-stats-value" :class="{ 'msv-win': teamWins(matchStats.team.pointsLost, matchStats.opposition.pointsLost, false) }">
              <span class="msv-total">{{ matchStats.team.pointsLost }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('pointsLost')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
            <div class="match-stats-label">Points given away</div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(matchStats.team.pointsLost, matchStats.opposition.pointsLost, false) }">
              <span class="msv-total">{{ matchStats.opposition.pointsLost }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('pointsLost')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>

            <div class="match-stats-value" :class="{ 'msv-win': teamWins(matchStats.team.pointDiff, matchStats.opposition.pointDiff) }">
              <span class="msv-total">{{ matchStats.team.pointDiff }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('pointDiff')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
            <div class="match-stats-label">Point diff</div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(matchStats.team.pointDiff, matchStats.opposition.pointDiff) }">
              <span class="msv-total">{{ matchStats.opposition.pointDiff }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('pointDiff')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
          </div>
          <div class="match-stats-spacer"></div>

          <div class="match-stats-group">
            <div class="match-stats-value" :class="{ 'msv-win': teamWins(matchStats.team.kills, matchStats.opposition.kills) }">
              <span class="msv-total">{{ matchStats.team.kills }}</span>
              <span class="msv-pct">{{ matchStats.team.killsPercent }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('kills')" :key="i"><span v-if="i > 0" class="set-div">|</span><span class="has-pct"><span>{{ val }}</span><span class="sub">{{ teamSetValues('killsPercent')[i] }}</span></span></template></div>
            </div>
            <div class="match-stats-label">Kills</div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(matchStats.team.kills, matchStats.opposition.kills) }">
              <span class="msv-total">{{ matchStats.opposition.kills }}</span>
              <span class="msv-pct">{{ matchStats.opposition.killsPercent }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('kills')" :key="i"><span v-if="i > 0" class="set-div">|</span><span class="has-pct"><span>{{ val }}</span><span class="sub">{{ oppSetValues('killsPercent')[i] }}</span></span></template></div>
            </div>

            <div class="match-stats-value" :class="{ 'msv-win': teamWins(matchStats.team.attackErrors, matchStats.opposition.attackErrors, false) }">
              <span class="msv-total">{{ matchStats.team.attackErrors }}</span>
              <span class="msv-pct">{{ matchStats.team.attackErrorsPercent }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('attackErrors')" :key="i"><span v-if="i > 0" class="set-div">|</span><span class="has-pct"><span>{{ val }}</span><span class="sub">{{ teamSetValues('attackErrorsPercent')[i] }}</span></span></template></div>
            </div>
            <div class="match-stats-label">Attack errors</div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(matchStats.team.attackErrors, matchStats.opposition.attackErrors, false) }">
              <span class="msv-total">{{ matchStats.opposition.attackErrors }}</span>
              <span class="msv-pct">{{ matchStats.opposition.attackErrorsPercent }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('attackErrors')" :key="i"><span v-if="i > 0" class="set-div">|</span><span class="has-pct"><span>{{ val }}</span><span class="sub">{{ oppSetValues('attackErrorsPercent')[i] }}</span></span></template></div>
            </div>

            <div class="match-stats-value" :class="{ 'msv-win': teamWins(matchStats.team.attackEfficiency, matchStats.opposition.attackEfficiency) }">
              <span class="msv-total">{{ matchStats.team.attackEfficiency }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('attackEfficiency')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
            <div class="match-stats-label">Attack efficiency</div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(matchStats.team.attackEfficiency, matchStats.opposition.attackEfficiency) }">
              <span class="msv-total">{{ matchStats.opposition.attackEfficiency }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('attackEfficiency')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
          </div>
          <div class="match-stats-spacer"></div>

          <div class="match-stats-group">
            <div class="match-stats-value" :class="{ 'msv-win': teamWins(matchStats.team.blocks, matchStats.opposition.blocks) }">
              <span class="msv-total">{{ matchStats.team.blocks }}</span>
              <span class="msv-pct">{{ matchStats.team.blocksPercent }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('blocks')" :key="i"><span v-if="i > 0" class="set-div">|</span><span class="has-pct"><span>{{ val }}</span><span class="sub">{{ teamSetValues('blocksPercent')[i] }}</span></span></template></div>
            </div>
            <div class="match-stats-label">Kill blocks</div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(matchStats.team.blocks, matchStats.opposition.blocks) }">
              <span class="msv-total">{{ matchStats.opposition.blocks }}</span>
              <span class="msv-pct">{{ matchStats.opposition.blocksPercent }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('blocks')" :key="i"><span v-if="i > 0" class="set-div">|</span><span class="has-pct"><span>{{ val }}</span><span class="sub">{{ oppSetValues('blocksPercent')[i] }}</span></span></template></div>
            </div>

            <div class="match-stats-value" :class="{ 'msv-win': teamWins(matchStats.team.blockErrors, matchStats.opposition.blockErrors, false) }">
              <span class="msv-total">{{ matchStats.team.blockErrors }}</span>
              <span class="msv-pct">{{ matchStats.team.blockErrorsPercent }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('blockErrors')" :key="i"><span v-if="i > 0" class="set-div">|</span><span class="has-pct"><span>{{ val }}</span><span class="sub">{{ teamSetValues('blockErrorsPercent')[i] }}</span></span></template></div>
            </div>
            <div class="match-stats-label">Block errors</div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(matchStats.team.blockErrors, matchStats.opposition.blockErrors, false) }">
              <span class="msv-total">{{ matchStats.opposition.blockErrors }}</span>
              <span class="msv-pct">{{ matchStats.opposition.blockErrorsPercent }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('blockErrors')" :key="i"><span v-if="i > 0" class="set-div">|</span><span class="has-pct"><span>{{ val }}</span><span class="sub">{{ oppSetValues('blockErrorsPercent')[i] }}</span></span></template></div>
            </div>
          </div>
          <div class="match-stats-spacer"></div>

          <div class="match-stats-group">
            <div class="match-stats-value" :class="{ 'msv-win': teamWins(matchStats.team.aces, matchStats.opposition.aces) }">
              <span class="msv-total">{{ matchStats.team.aces }}</span>
              <span class="msv-pct">{{ matchStats.team.acesPercent }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('aces')" :key="i"><span v-if="i > 0" class="set-div">|</span><span class="has-pct"><span>{{ val }}</span><span class="sub">{{ teamSetValues('acesPercent')[i] }}</span></span></template></div>
            </div>
            <div class="match-stats-label">Aces</div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(matchStats.team.aces, matchStats.opposition.aces) }">
              <span class="msv-total">{{ matchStats.opposition.aces }}</span>
              <span class="msv-pct">{{ matchStats.opposition.acesPercent }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('aces')" :key="i"><span v-if="i > 0" class="set-div">|</span><span class="has-pct"><span>{{ val }}</span><span class="sub">{{ oppSetValues('acesPercent')[i] }}</span></span></template></div>
            </div>

            <div class="match-stats-value" :class="{ 'msv-win': teamWins(matchStats.team.serveErrors, matchStats.opposition.serveErrors, false) }">
              <span class="msv-total">{{ matchStats.team.serveErrors }}</span>
              <span class="msv-pct">{{ matchStats.team.serveErrorsPercent }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('serveErrors')" :key="i"><span v-if="i > 0" class="set-div">|</span><span class="has-pct"><span>{{ val }}</span><span class="sub">{{ teamSetValues('serveErrorsPercent')[i] }}</span></span></template></div>
            </div>
            <div class="match-stats-label">Serve errors</div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(matchStats.team.serveErrors, matchStats.opposition.serveErrors, false) }">
              <span class="msv-total">{{ matchStats.opposition.serveErrors }}</span>
              <span class="msv-pct">{{ matchStats.opposition.serveErrorsPercent }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('serveErrors')" :key="i"><span v-if="i > 0" class="set-div">|</span><span class="has-pct"><span>{{ val }}</span><span class="sub">{{ oppSetValues('serveErrorsPercent')[i] }}</span></span></template></div>
            </div>

            <div class="match-stats-value" :class="{ 'msv-win': teamWins(servingStats.serveRating, servingStats.oppServeRating) }">
              <span class="msv-total">{{ servingStats.serveRating }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('serveRating')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
            <div class="match-stats-label">Serve rating</div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(servingStats.serveRating, servingStats.oppServeRating) }">
              <span class="msv-total">{{ servingStats.oppServeRating }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('serveRating')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>

            <div class="match-stats-value" :class="{ 'msv-win': teamWins(receivingStats.passRating, receivingStats.oppPassRating) }">
              <span class="msv-total">{{ receivingStats.passRating }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('passRating')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
            <div class="match-stats-label">Pass rating</div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(receivingStats.passRating, receivingStats.oppPassRating) }">
              <span class="msv-total">{{ receivingStats.oppPassRating }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('passRating')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
          </div>
          <div class="match-stats-spacer"></div>
          <div class="match-stats-group">
            <div class="match-stats-value" :class="{ 'msv-win': teamWins(matchStats.team.digs, matchStats.opposition.digs) }">
              <span class="msv-total">{{ matchStats.team.digs }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('digs')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
            <div class="match-stats-label">Digs</div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(matchStats.team.digs, matchStats.opposition.digs) }">
              <span class="msv-total">{{ matchStats.opposition.digs }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('digs')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
          </div>
          <div class="match-stats-spacer"></div>

          <div class="match-stats-group">
            <div class="match-stats-value" :class="{ 'msv-win': teamWins(matchStats.team.otherErrors, matchStats.opposition.otherErrors, false) }">
              <span class="msv-total">{{ matchStats.team.otherErrors }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('otherErrors')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
            <div class="match-stats-label">Other errors</div>
            <div class="match-stats-value" :class="{ 'msv-win': oppWins(matchStats.team.otherErrors, matchStats.opposition.otherErrors, false) }">
              <span class="msv-total">{{ matchStats.opposition.otherErrors }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('otherErrors')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
          </div>
          <div v-if="summaryMode" class="match-stats-group">
            <div class="match-stats-value">
              <span class="msv-total">{{ matchStats.opposition.errorsTotal }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in oppSetValues('errorsTotal')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
            <div class="match-stats-label">Opposition errors</div>
            <div class="match-stats-value">
              <span class="msv-total">{{ matchStats.team.errorsTotal }}</span>
              <div v-if="showSetBreakdown" class="msv-sets"><template v-for="(val, i) in teamSetValues('errorsTotal')" :key="i"><span v-if="i > 0" class="set-div">|</span><span>{{ val }}</span></template></div>
            </div>
          </div>
        </div>
      </section>
      <section class="stats-section" v-if="currentTab === 'Serve' || (inline && currentTab === 'All')">
        <div class="stat-grid" v-dim-zero-stats="isNonParticipantInView">
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Aces</span>
            <span class="stat-value">{{ servingStats.aces }}</span>
            <span class="stat-percent">{{ servingStats.acesPercent }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('aces')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span><span class="pct">{{ teamSetValues('acesPercent')[i] }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">#3 serves</span>
            <span class="stat-value">{{ servingStats.threes }}</span>
            <span class="stat-percent">{{ servingStats.threesPercent }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('serveThrees')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span><span class="pct">{{ teamSetValues('serveThreesPercent')[i] }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">#2 serves</span>
            <span class="stat-value">{{ servingStats.twos }}</span>
            <span class="stat-percent">{{ servingStats.twosPercent }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('serveTwos')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span><span class="pct">{{ teamSetValues('serveTwosPercent')[i] }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">#1 serve</span>
            <span class="stat-value">{{ servingStats.ones }}</span>
            <span class="stat-percent">{{ servingStats.onesPercent }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('serveOnes')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span><span class="pct">{{ teamSetValues('serveOnesPercent')[i] }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Serve errors</span>
            <span class="stat-value">{{ servingStats.errors }}</span>
            <span class="stat-percent">{{ servingStats.errorsPercent }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('serveErrors')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span><span class="pct">{{ teamSetValues('serveErrorsPercent')[i] }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Points on serve</span>
            <span class="stat-value">{{ servingStats.pointsOnServe }}</span>
            <span class="stat-percent">{{ servingStats.pointsOnServePercent }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('pointsOnServe')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span><span class="pct">{{ teamSetValues('pointsOnServePercent')[i] }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Total serves</span>
            <span class="stat-value">{{ servingStats.totalServes }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('totalServes')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Serve rating</span>
            <span class="stat-value">{{ servingStats.serveRating }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('serveRating')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span>
              </div>
            </div>
          </div>
          <!-- <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Opp. serve rating</span>
            <span class="stat-value">{{ servingStats.oppServeRating }}</span>
            <span v-if="!isPlayerFiltered" class="stat-subtext">
              ({{ servingStats.passErrors }}*4 + {{ servingStats.passOnes }}*3 + {{ servingStats.passTwos }}*2 + {{ servingStats.passThrees }}*1 + {{ servingStats.oppPointsOnServe }}*0.5) / {{ servingStats.oppTotalServes }} = {{ servingStats.oppServeRatingValue.toFixed(2) }}
            </span>
          </div> -->
        </div>
      </section>
      <section class="stats-section" v-if="currentTab === 'Receive' || (inline && currentTab === 'All')">
        <div class="stat-grid" v-dim-zero-stats="isNonParticipantInView">
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">#3 passes</span>
            <span class="stat-value">{{ receivingStats.threes }}</span>
            <span class="stat-percent">{{ receivingStats.threesPercent }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('passThrees')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span><span class="pct">{{ teamSetValues('passThreesPercent')[i] }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">#2 passes</span>
            <span class="stat-value">{{ receivingStats.twos }}</span>
            <span class="stat-percent">{{ receivingStats.twosPercent }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('passTwos')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span><span class="pct">{{ teamSetValues('passTwosPercent')[i] }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">#1 passes</span>
            <span class="stat-value">{{ receivingStats.ones }}</span>
            <span class="stat-percent">{{ receivingStats.onesPercent }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('passOnes')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span><span class="pct">{{ teamSetValues('passOnesPercent')[i] }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Passing errors</span>
            <span class="stat-value">{{ receivingStats.errors }}</span>
            <span class="stat-percent">{{ receivingStats.errorsPercent }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('passErrors')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span><span class="pct">{{ teamSetValues('passErrorsPercent')[i] }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Passing attempts</span>
            <span class="stat-value">{{ receivingStats.passAttempts }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('passAttempts')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Total receives</span>
            <span class="stat-value">{{ receivingStats.totalReceives }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('totalReceives')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Opp. serve errors</span>
            <span class="stat-value">{{ receivingStats.oppServeErrors }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('oppServeErrors')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Pass rating</span>
            <span class="stat-value">{{ receivingStats.passRating }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('passRating')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span>
              </div>
            </div>
          </div>
          <!-- <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Opp. pass rating</span>
            <span class="stat-value">{{ receivingStats.oppPassRating }}</span>
            <span v-if="!isPlayerFiltered" class="stat-subtext">
              ({{ receivingStats.foursServe }}*0 + {{ receivingStats.threesServe }}*1 + {{ receivingStats.twosServe }}*2 + {{ receivingStats.onesServe }}*3) / {{ receivingStats.oppPassAttempts }} = {{ receivingStats.oppPassRatingValue.toFixed(2) }}
            </span>
          </div> -->
        </div>
      </section>
      <section class="stats-section" v-if="currentTab === 'Attack' || (inline && currentTab === 'All')">
        <div class="stat-grid" v-dim-zero-stats="isNonParticipantInView">
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Kills</span>
            <span class="stat-value">{{ attackStats.kills }}</span>
            <span class="stat-percent">{{ attackStats.killsPercent }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('kills')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span><span class="pct">{{ teamSetValues('killsPercent')[i] }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Attack errors</span>
            <span class="stat-value">{{ attackStats.errors }}</span>
            <span class="stat-percent">{{ attackStats.errorsPercent }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('attackErrors')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span><span class="pct">{{ teamSetValues('attackErrorsPercent')[i] }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Attack attempts</span>
            <span class="stat-value">{{ attackStats.attempts }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('attackAttempts')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Attack efficiency</span>
            <span class="stat-value">{{ attackStats.efficiency }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('attackEfficiency')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span>
              </div>
            </div>
          </div>
          <!-- <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Opp. attack efficiency</span>
            <span class="stat-value">{{ attackStats.oppEfficiency }}</span>
          </div> -->
        </div>
      </section>
      <section class="stats-section" v-if="currentTab === 'Block' || (inline && currentTab === 'All')">
        <div class="stat-grid" v-dim-zero-stats="isNonParticipantInView">
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Kill blocks</span>
            <span class="stat-value">{{ blockStats.killBlocks }}</span>
            <span class="stat-subtext">solo {{ blockStats.killBlocksSolo }} / {{ blockStats.killBlocksJoint }} double</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('blocks')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Successful blocks</span>
            <span class="stat-value">{{ blockStats.successfulBlocks }}</span>
            <span class="stat-subtext">solo {{ blockStats.successfulBlocksSolo }} / {{ blockStats.successfulBlocksJoint }} double</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('successfulBlocks')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span>
              </div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Block errors</span>
            <span class="stat-value">{{ blockStats.blockErrors }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('blockErrors')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section class="stats-section" v-if="currentTab === 'Defence' || (inline && currentTab === 'All')">
        <div class="stat-grid" v-dim-zero-stats="isNonParticipantInView">
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Digs</span>
            <span class="stat-value">{{ matchStats.team.digs }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('digs')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section class="stats-section" v-if="currentTab === 'Other Errors' || (inline && currentTab === 'All')">
        <div class="stat-grid" v-dim-zero-stats="isNonParticipantInView">
          <div class="stat-card">
            <div class="stat-main"><span class="stat-label">Other errors</span>
            <span class="stat-value">{{ matchStats.team.otherErrors }}</span>
            </div>
            <div v-if="showSetBreakdown" class="stat-sets">
              <div v-for="(val, i) in teamSetValues('otherErrors')" :key="i" :class="['stat-set-col', { 'non-participant': nonParticipantSetIndices.has(i) }]">
                <span>{{ val }}</span>
              </div>
            </div>
          </div>
          <div v-if="summaryMode" class="stat-card">
            <div class="stat-main"><span class="stat-label">Opposition errors</span>
            <span class="stat-value">{{ matchStats.opposition.errorsTotal }}</span></div>
          </div>
          <!-- <div v-if="summaryMode" class="stat-card">
            <div class="stat-main"><span class="stat-label">Opposition errors (opposition)</span>
            <span class="stat-value">{{ matchStats.team.errorsTotal }}</span>
          </div> -->
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { store } from '../store.js';

function markNonParticipantZeros(el, binding) {
  el.querySelectorAll('.stat-main').forEach(main => {
    const valueEl = main.querySelector('.stat-value');
    const text = valueEl ? valueEl.textContent.trim() : '';
    const num = parseFloat(text.replace('%', ''));
    const isZero = text === '-' || (!isNaN(num) && num === 0);
    main.classList.toggle('zero-non-participant', !!(binding.value && isZero));
  });
}

export default {
  directives: {
    dimZeroStats: {
      updated: markNonParticipantZeros
    }
  },
  name: 'StatsDisplay',
  props: {
    isCollapsed: {
      type: Boolean,
      default: false
    },
    fullscreen: {
      type: Boolean,
      default: false
    },
    summaryMode: {
      type: Boolean,
      default: false
    },
    inline: {
      type: Boolean,
      default: false
    },
    sidebarCollapsed: {
      type: Boolean,
      default: false
    },
    points: {
      type: Array,
      default: null
    },
    activeSetId: {
      type: [String, Number],
      default: null
    },
    selectedPlayerNumber: {
      type: Number,
      default: null
    },
    tab: {
      type: String,
      default: null
    },
    sets: {
      type: Array,
      default: null
    },
    playerParticipated: {
      type: Boolean,
      default: null
    }
  },
  data() {
    return {
      activeTab: 'All',
      tabs: ['All', 'Serve', 'Receive', 'Attack', 'Block', 'Defence', 'Other Errors']
    };
  },
  computed: {
    currentTab() {
      return this.tab !== null ? this.tab : this.activeTab;
    },
    showSetBreakdown() {
      return !!(this.sets && this.sets.length > 0);
    },
    isNonParticipantInView() {
      if (!this.isPlayerFiltered) return false;
      if (this.playerParticipated === false) return true;
      if (this.sets && this.sets.length > 0) {
        return this.nonParticipantSetIndices.size === this.sets.length;
      }
      return false;
    },
    nonParticipantSetIndices() {
      if (!this.isPlayerFiltered || !this.sets) return new Set();
      const num = this.selectedPlayerNumber;
      return new Set(
        this.sets.reduce((acc, set, i) => {
          const lineup = Array.isArray(set.startingLineup) ? set.startingLineup : [];
          if (lineup.some(item => item.player?.number === num)) return acc;
          if (set.startingLibero?.number === num) return acc;
          const points = Array.isArray(set.points) ? set.points : [];
          if (points.some(p => (p.currentLineupNumbers || []).includes(num))) return acc;
          acc.push(i);
          return acc;
        }, [])
      );
    },
    displayedTeamScore() {
      return this.activeSetId === 'total' ? this.cumulativeTeamPoints : this.currentSetTeamScore;
    },
    displayedOppScore() {
      return this.activeSetId === 'total' ? this.cumulativeOppositionPoints : this.currentSetOppositionScore;
    },
    perSetStats() {
      if (!this.sets || !this.sets.length) return [];
      const playerNumber = this.isPlayerFiltered ? this.selectedPlayerNumber : null;
      return this.sets.map(set => this.setStatsFromPoints(set.points || [], set, playerNumber));
    },
    currentSetPoints() {
      if (Array.isArray(this.points)) {
        return this.points;
      }
      const currentSet = store.getCurrentSet();
      return currentSet ? currentSet.points : [];
    },
    isPlayerFiltered() {
      return typeof this.selectedPlayerNumber === 'number';
    },
    teamName() {
      return (store && store.match && store.match.metadata && store.match.metadata.teamName) || 'Our team';
    },
    oppositionName() {
      return (store && store.match && store.match.metadata && store.match.metadata.opposition) || 'Opposition';
    },
    matchSetsWon() {
      return (store && store.match && typeof store.match.setsWon === 'number') ? store.match.setsWon : 0;
    },
    matchSetsLost() {
      return (store && store.match && typeof store.match.setsLost === 'number') ? store.match.setsLost : 0;
    },
    teamSetPointsString() {
      const sets = Array.isArray(store.match.sets) ? store.match.sets : [];
      const vals = sets.map(s => (s && s.score && typeof s.score.won === 'number') ? s.score.won : 0);
      const total = vals.reduce((a, b) => a + b, 0);
      return vals.join(', ') + (vals.length ? ` (${total})` : '');
    },
    oppositionSetPointsString() {
      const sets = Array.isArray(store.match.sets) ? store.match.sets : [];
      const vals = sets.map(s => (s && s.score && typeof s.score.lost === 'number') ? s.score.lost : 0);
      const total = vals.reduce((a, b) => a + b, 0);
      return vals.join(', ') + (vals.length ? ` (${total})` : '');
    },
    cumulativeTeamPoints() {
      const sets = Array.isArray(store.match.sets) ? store.match.sets : [];
      return sets.reduce((sum, s) => sum + ((s && s.score && typeof s.score.won === 'number') ? s.score.won : 0), 0);
    },
    cumulativeOppositionPoints() {
      const sets = Array.isArray(store.match.sets) ? store.match.sets : [];
      return sets.reduce((sum, s) => sum + ((s && s.score && typeof s.score.lost === 'number') ? s.score.lost : 0), 0);
    },
    currentSetTeamScore() {
      if (!this.activeSetId || this.activeSetId === 'total') return null;
      const set = (Array.isArray(store.match.sets) ? store.match.sets : []).find(s => String(s.id) === String(this.activeSetId));
      return set && set.score && typeof set.score.won === 'number' ? set.score.won : 0;
    },
    currentSetOppositionScore() {
      if (!this.activeSetId || this.activeSetId === 'total') return null;
      const set = (Array.isArray(store.match.sets) ? store.match.sets : []).find(s => String(s.id) === String(this.activeSetId));
      return set && set.score && typeof set.score.lost === 'number' ? set.score.lost : 0;
    },
    matchStats() {
      const points = this.currentSetPoints;
      const isWon = point => point.scoreAction.outcome === 'Won';
      const isLost = point => point.scoreAction.outcome === 'Lost';
      const selected = this.selectedPlayerNumber;
      const hasSelected = point => {
        const primary = point.scoreAction.player && point.scoreAction.player.number;
        const secondary = point.scoreAction.secondPlayer && point.scoreAction.secondPlayer.number;
        return [primary, secondary].includes(selected);
      };

      const count = predicate => points.filter(predicate).length;
      const rallyAttacks = points.reduce((total, point) => {
        const bucket = point.duringRally?.attacks || {};
        return total + Object.values(bucket).reduce((sum, value) => sum + value, 0);
      }, 0);
      const nonTerminalBlocks = points.reduce((total, point) => {
        const solo = point.duringRally?.blocksSolo || {};
        const joint = point.duringRally?.blocksJoint || {};
        return total +
          Object.values(solo).reduce((sum, value) => sum + value, 0) +
          Object.values(joint).reduce((sum, value) => sum + value, 0) * 0.5;
      }, 0);
      const rallyDigs = points.reduce((total, point) => {
        const bucket = point.duringRally?.digs || {};
        return total + Object.values(bucket).reduce((sum, value) => sum + value, 0);
      }, 0);

      const team = {
        kills: count(point => isWon(point) && point.scoreAction.pointDetail === 'Kill'),
        blocks: count(point => isWon(point) && ['Kill block (solo)', 'Kill block (assist)'].includes(point.scoreAction.pointDetail)),
        aces: count(point => isWon(point) && point.scoreAction.pointDetail === 'Ace'),
        attackErrors: count(
          point =>
            isLost(point) &&
            ['Attack error', 'Opp. kill block'].includes(point.scoreAction.pointDetail)
        ),
        blockErrors: count(point => isLost(point) && point.scoreAction.pointDetail === 'Block error'),
        passErrors: count(point => point.startPoint === 'Receive' && point.receiveAction.rating === 0),
        serveErrors: count(point => isLost(point) && point.scoreAction.pointDetail === 'Serve error'),
        pointsOnServe: count(point => isWon(point) && point.startPoint === 'Serve'),
        otherErrors: count(point =>
          isLost(point) &&
          (point.scoreAction.pointDetail === 'Other error' || point.scoreAction.pointDetail === 'Rotation Error')
        ),
        digs: rallyDigs
      };

      const opposition = {
        kills: count(point => isLost(point) && point.scoreAction.pointDetail === 'Opp. kill'),
        blocks: count(point => isLost(point) && point.scoreAction.pointDetail === 'Opp. kill block'),
        aces: count(point => isLost(point) && point.scoreAction.pointDetail === 'Opp. ace'),
        attackErrors: count(
          point =>
            isWon(point) &&
            ['Opp. attack error', 'Kill block (solo)', 'Kill block (assist)'].includes(point.scoreAction.pointDetail)
        ),
        blockErrors: count(point => isWon(point) && point.scoreAction.pointDetail === 'Opp. block error'),
        passErrors: count(point => point.scoreAction.pointDetail === 'Ace'),
        serveErrors: count(point => isWon(point) && point.scoreAction.pointDetail === 'Opp. serve error'),
        pointsOnServe: count(point => isLost(point) && point.startPoint === 'Receive'),
        otherErrors: count(point =>
          isWon(point) &&
          (point.scoreAction.pointDetail === 'Opp. other error' || point.scoreAction.pointDetail === 'Opposition Rotation Error')
        ),
        digs: rallyAttacks + nonTerminalBlocks
      };

      const teamOppErrors = count(
        point =>
          isLost(point) &&
          (point.scoreAction.pointDetail === 'Attack error' ||
            point.scoreAction.pointDetail === 'Block error' ||
            point.scoreAction.pointDetail === 'Serve error' ||
            point.scoreAction.pointDetail === 'Other error' ||
            point.scoreAction.pointDetail === 'Rotation Error')
      );
      const oppositionOppErrors = count(
        point =>
          isWon(point) &&
          (point.scoreAction.pointDetail === 'Opp. attack error' ||
            point.scoreAction.pointDetail === 'Opp. block error' ||
            point.scoreAction.pointDetail === 'Opp. serve error' ||
            point.scoreAction.pointDetail === 'Opp. other error' ||
            point.scoreAction.pointDetail === 'Opposition Rotation Error')
      );

      if (this.isPlayerFiltered) {
        const teamOppErrorsFiltered = count(
          point =>
            isLost(point) &&
            (point.scoreAction.pointDetail === 'Attack error' ||
              point.scoreAction.pointDetail === 'Block error' ||
              point.scoreAction.pointDetail === 'Serve error' ||
              point.scoreAction.pointDetail === 'Other error' ||
              point.scoreAction.pointDetail === 'Rotation Error') &&
            hasSelected(point)
        );
        const filtered = {
          kills: count(point => isWon(point) && point.scoreAction.pointDetail === 'Kill' && hasSelected(point)),
          blocks: count(
            point =>
              isWon(point) &&
              ['Kill block (solo)', 'Kill block (assist)'].includes(point.scoreAction.pointDetail) &&
              hasSelected(point)
          ),
          aces: count(point => isWon(point) && point.scoreAction.pointDetail === 'Ace' && hasSelected(point)),
          attackErrors: count(
            point =>
              isLost(point) &&
              ['Attack error', 'Opp. kill block'].includes(point.scoreAction.pointDetail) &&
              hasSelected(point)
          ),
          blockErrors: count(point => isLost(point) && point.scoreAction.pointDetail === 'Block error' && hasSelected(point)),
          passErrors: count(point => point.startPoint === 'Receive' && point.receiveAction.rating === 0 && point.receiveAction.player && point.receiveAction.player.number === selected),
          serveErrors: count(point => isLost(point) && point.scoreAction.pointDetail === 'Serve error' && hasSelected(point)),
          pointsOnServe: count(point => isWon(point) && point.startPoint === 'Serve' && point.serveAction.player && point.serveAction.player.number === selected),
          otherErrors: count(point =>
            isLost(point) &&
            (point.scoreAction.pointDetail === 'Other error' || point.scoreAction.pointDetail === 'Rotation Error') &&
            hasSelected(point)
          ),
          digs: points.reduce((total, point) => {
            const bucket = point.duringRally?.digs || {};
            return total + (bucket[selected] || 0);
          }, 0)
        };
        return {
          team: {
            ...filtered,
            attackEfficiency: this.attackStats.efficiency,
            serveRating: this.servingStats.serveRating,
            passRating: this.receivingStats.passRating,
            pointsWon: filtered.kills + filtered.blocks + filtered.aces,
            pointsLost: filtered.attackErrors + filtered.blockErrors + filtered.passErrors + filtered.serveErrors + filtered.otherErrors,
            pointDiff: (filtered.kills + filtered.blocks + filtered.aces) - (filtered.attackErrors + filtered.blockErrors + filtered.passErrors + filtered.serveErrors + filtered.otherErrors),
            errorsTotal: teamOppErrorsFiltered
          },
          opposition: {
            kills: '-',
            blocks: '-',
            aces: '-',
            attackErrors: '-',
            blockErrors: '-',
            passErrors: '-',
            serveErrors: '-',
            pointsOnServe: '-',
            otherErrors: '-',
            digs: '-',
            pointsWon: '-',
            pointsLost: '-',
            pointDiff: '-',
            attackEfficiency: '-',
            serveRating: '-',
            passRating: '-',
            errorsTotal: '-',
            killsPercent: '-',
            attackErrorsPercent: '-',
            acesPercent: '-',
            serveErrorsPercent: '-',
            blocksPercent: '-',
            blockErrorsPercent: '-'
          }
        };
      }

      const teamAttempts = team.kills + team.attackErrors + rallyAttacks;
      const oppositionAttempts = opposition.kills + opposition.attackErrors + rallyDigs;
      const efficiency = (kills, errors, attempts) => {
        const total = attempts ?? (kills + errors);
        return total ? `${(((kills - errors) / total) * 100).toFixed(1)}%` : '0.0%';
      };
      const pct = (n, d) => d ? `${((n / d) * 100).toFixed(1)}%` : '-';
      const totalServes = points.filter(p => p.startPoint === 'Serve' && !p.serveAction?.call).length;
      const serveErrors0 = points.filter(p => p.startPoint === 'Serve' && !p.serveAction?.call && p.serveAction?.rating === 0).length;
      const oppTotalServes = points.filter(p => p.startPoint === 'Receive').length;

      return {
        team: {
          ...team,
          attackEfficiency: efficiency(team.kills, team.attackErrors, teamAttempts),
          pointsWon: team.kills + team.blocks + team.aces,
          pointsLost: team.attackErrors + team.blockErrors + team.passErrors + team.serveErrors + team.otherErrors,
          pointDiff: (team.kills + team.blocks + team.aces) - (team.attackErrors + team.blockErrors + team.passErrors + team.serveErrors + team.otherErrors),
          errorsTotal: teamOppErrors,
          killsPercent: pct(team.kills, teamAttempts),
          attackErrorsPercent: pct(team.attackErrors, teamAttempts),
          acesPercent: pct(team.aces, totalServes),
          serveErrorsPercent: pct(serveErrors0, totalServes),
          blocksPercent: pct(team.blocks, team.blocks + team.blockErrors),
          blockErrorsPercent: pct(team.blockErrors, team.blocks + team.blockErrors)
        },
        opposition: {
          ...opposition,
          attackEfficiency: efficiency(opposition.kills, opposition.attackErrors, oppositionAttempts),
          pointsWon: opposition.kills + opposition.blocks + opposition.aces,
          pointsLost: opposition.attackErrors + opposition.blockErrors + opposition.passErrors + opposition.serveErrors + opposition.otherErrors,
          pointDiff: (opposition.kills + opposition.blocks + opposition.aces) - (opposition.attackErrors + opposition.blockErrors + opposition.passErrors + opposition.serveErrors + opposition.otherErrors),
          errorsTotal: oppositionOppErrors,
          killsPercent: pct(opposition.kills, oppositionAttempts),
          attackErrorsPercent: pct(opposition.attackErrors, oppositionAttempts),
          acesPercent: pct(opposition.aces, oppTotalServes),
          serveErrorsPercent: pct(opposition.serveErrors, oppTotalServes),
          blocksPercent: pct(opposition.blocks, opposition.blocks + opposition.blockErrors),
          blockErrorsPercent: pct(opposition.blockErrors, opposition.blocks + opposition.blockErrors)
        }
      };
    },
    servingStats() {
      const selected = this.selectedPlayerNumber;
      const servePoints = this.currentSetPoints.filter(point => {
        if (point.startPoint !== 'Serve' || point.serveAction.call) {
          return false;
        }
        if (!this.isPlayerFiltered) {
          return true;
        }
        return point.serveAction.player && point.serveAction.player.number === selected;
      });
      const totalServes = servePoints.length;

      const countByRating = rating => servePoints.filter(point => point.serveAction.rating === rating).length;
      const aces = servePoints.filter(point => point.serveAction.rating === 4 || point.scoreAction.pointDetail === 'Ace').length;
      const threes = countByRating(3);
      const twos = countByRating(2);
      const ones = countByRating(1);
      const errors = countByRating(0);
      const pointsOnServe = servePoints.filter(point => point.scoreAction.outcome === 'Won').length;

      const percent = count => (totalServes ? `${((count / totalServes) * 100).toFixed(1)}%` : '0.0%');
      const serveRatingValue = totalServes
        ? ((aces * 4 + threes * 3 + twos * 2 + ones * 1 + pointsOnServe * 0.5) / totalServes)
        : 0;
      const receivePoints = this.currentSetPoints.filter(point => point.startPoint === 'Receive');
      const oppTotalServes = receivePoints.length;
      const oppPointsOnServe = receivePoints.filter(point => point.scoreAction.outcome === 'Lost').length;
      const passErrors = receivePoints.filter(point => point.receiveAction.rating === 0).length;
      const passOnes = receivePoints.filter(point => point.receiveAction.rating === 1).length;
      const passTwos = receivePoints.filter(point => point.receiveAction.rating === 2).length;
      const passThrees = receivePoints.filter(point => point.receiveAction.rating === 3).length;
      const oppServeRatingValue = oppTotalServes
        ? ((passErrors * 4 + passOnes * 3 + passTwos * 2 + passThrees * 1 + oppPointsOnServe * 0.5) / oppTotalServes)
        : 0;

      return {
        aces,
        acesPercent: percent(aces),
        threes,
        threesPercent: percent(threes),
        twos,
        twosPercent: percent(twos),
        ones,
        onesPercent: percent(ones),
        errors,
        errorsPercent: percent(errors),
        pointsOnServe,
        pointsOnServePercent: percent(pointsOnServe),
        totalServes,
        serveRating: serveRatingValue.toFixed(2),
        oppServeRating: this.isPlayerFiltered ? '-' : oppServeRatingValue.toFixed(2),
        oppServeRatingValue,
        oppTotalServes,
        oppPointsOnServe,
        passErrors,
        passOnes,
        passTwos,
        passThrees
      };
    },
    receivingStats() {
      const selected = this.selectedPlayerNumber;
      const receivePoints = this.currentSetPoints.filter(point => {
        if (point.startPoint !== 'Receive') {
          return false;
        }
        if (!this.isPlayerFiltered) {
          return true;
        }
        return point.receiveAction.player && point.receiveAction.player.number === selected;
      });
      const totalReceives = receivePoints.length;
      const passAttempts = receivePoints.filter(point => point.receiveAction.rating !== null).length;

      const countByRating = rating => receivePoints.filter(point => point.receiveAction.rating === rating).length;
      const threes = countByRating(3);
      const twos = countByRating(2);
      const ones = countByRating(1);
      const errors = countByRating(0);
      const oppServeErrors = receivePoints.filter(point => point.scoreAction.pointDetail === 'Opp. serve error').length;

      const percent = count => (passAttempts ? `${((count / passAttempts) * 100).toFixed(1)}%` : '0.0%');
      const passRatingValue = passAttempts
        ? ((threes * 3 + twos * 2 + ones * 1) / passAttempts)
        : 0;

      const servePoints = this.currentSetPoints.filter(point => point.startPoint === 'Serve' && !point.serveAction.call);
      const totalServes = servePoints.length;
      const serveErrors = servePoints.filter(point => point.serveAction.rating === 0).length;
      const threesServe = servePoints.filter(point => point.serveAction.rating === 3).length;
      const twosServe = servePoints.filter(point => point.serveAction.rating === 2).length;
      const onesServe = servePoints.filter(point => point.serveAction.rating === 1).length;
      const foursServe = servePoints.filter(point => point.serveAction.rating === 4).length;
      const oppPassAttempts = Math.max(totalServes - serveErrors, 0);
      const oppPassRatingValue = oppPassAttempts
        ? ((foursServe * 0 + threesServe * 1 + twosServe * 2 + onesServe * 3) / oppPassAttempts)
        : 0;

      return {
        threes,
        threesPercent: percent(threes),
        twos,
        twosPercent: percent(twos),
        ones,
        onesPercent: percent(ones),
        errors,
        errorsPercent: percent(errors),
        passAttempts,
        totalReceives,
        oppServeErrors,
        passRating: passRatingValue.toFixed(2),
        oppPassRating: this.isPlayerFiltered ? '-' : oppPassRatingValue.toFixed(2),
        oppPassAttempts,
        oppPassRatingValue,
        foursServe,
        threesServe,
        twosServe,
        onesServe
      };
    },
    attackStats() {
      const points = this.currentSetPoints;
      const selected = this.selectedPlayerNumber;
      const hasSelected = point => {
        const primary = point.scoreAction.player && point.scoreAction.player.number;
        const secondary = point.scoreAction.secondPlayer && point.scoreAction.secondPlayer.number;
        return [primary, secondary].includes(selected);
      };
      const kills = points.filter(
        point =>
          point.scoreAction.outcome === 'Won' &&
          point.scoreAction.pointDetail === 'Kill' &&
          (!this.isPlayerFiltered || hasSelected(point))
      ).length;
      const errors = points.filter(
        point =>
          point.scoreAction.outcome === 'Lost' &&
          ['Attack error', 'Opp. kill block'].includes(point.scoreAction.pointDetail) &&
          (!this.isPlayerFiltered || hasSelected(point))
      ).length;
      const rallyAttacks = points.reduce((total, point) => {
        const bucket = point.duringRally?.attacks || {};
        if (!this.isPlayerFiltered) {
          return total + Object.values(bucket).reduce((sum, count) => sum + count, 0);
        }
        return total + (bucket[selected] || 0);
      }, 0);
      const attempts = kills + errors + rallyAttacks;
      const percent = count => (attempts ? `${((count / attempts) * 100).toFixed(1)}%` : '0.0%');
      const efficiency = attempts
        ? `${(((kills - errors) / attempts) * 100).toFixed(1)}%`
        : '0.0%';
      const oppEfficiency = this.isPlayerFiltered ? '-' : (() => {
        const oppKills = points.filter(point => point.scoreAction.outcome === 'Lost' && point.scoreAction.pointDetail === 'Opp. kill').length;
        const oppErrors = points.filter(
          point =>
            point.scoreAction.outcome === 'Won' &&
            ['Opp. attack error', 'Kill block (solo)', 'Kill block (assist)'].includes(point.scoreAction.pointDetail)
        ).length;
        const rallyDigs = points.reduce((total, point) => {
          const bucket = point.duringRally?.digs || {};
          return total + Object.values(bucket).reduce((sum, count) => sum + count, 0);
        }, 0);
        const oppAttempts = oppKills + oppErrors + rallyDigs;
        return oppAttempts
          ? `${(((oppKills - oppErrors) / oppAttempts) * 100).toFixed(1)}%`
          : '0.0%';
      })();

      return {
        kills,
        killsPercent: percent(kills),
        errors,
        errorsPercent: percent(errors),
        attempts,
        efficiency,
        oppEfficiency
      };
    },
    blockStats() {
      const points = this.currentSetPoints;
      const selected = this.selectedPlayerNumber;
      const hasSelected = point => {
        const primary = point.scoreAction.player && point.scoreAction.player.number;
        const secondary = point.scoreAction.secondPlayer && point.scoreAction.secondPlayer.number;
        return [primary, secondary].includes(selected);
      };
      const killBlocksSolo = points.filter(
        point =>
          point.scoreAction.outcome === 'Won' &&
          point.scoreAction.pointDetail === 'Kill block (solo)' &&
          (!this.isPlayerFiltered || hasSelected(point))
      ).length;
      const killBlocksJoint = this.isPlayerFiltered
        ? points.reduce((total, point) => {
            if (
              point.scoreAction.outcome !== 'Won' ||
              point.scoreAction.pointDetail !== 'Kill block (assist)'
            ) {
              return total;
            }
            const primary = point.scoreAction.player && point.scoreAction.player.number;
            const secondary = point.scoreAction.secondPlayer && point.scoreAction.secondPlayer.number;
            if (primary === selected) {
              total += 0.5;
            }
            if (secondary === selected) {
              total += 0.5;
            }
            return total;
          }, 0)
        : points.filter(
            point =>
              point.scoreAction.outcome === 'Won' &&
              point.scoreAction.pointDetail === 'Kill block (assist)'
          ).length;
      const killBlocks = killBlocksSolo + killBlocksJoint;
      const nonTerminalSolo = points.reduce((total, point) => {
        const bucket = point.duringRally?.blocksSolo || {};
        if (!this.isPlayerFiltered) {
          return total + Object.values(bucket).reduce((sum, count) => sum + count, 0);
        }
        return total + (bucket[selected] || 0);
      }, 0);
      const nonTerminalJoint = points.reduce((total, point) => {
        const bucket = point.duringRally?.blocksJoint || {};
        if (!this.isPlayerFiltered) {
          return total + Object.values(bucket).reduce((sum, count) => sum + count, 0) * 0.5;
        }
        return total + (bucket[selected] || 0) * 0.5;
      }, 0);
      const successfulBlocksSolo = killBlocksSolo + nonTerminalSolo;
      const successfulBlocksJoint = killBlocksJoint + nonTerminalJoint;
      const successfulBlocks = successfulBlocksSolo + successfulBlocksJoint;
      const blockErrors = points.filter(
        point =>
          point.scoreAction.outcome === 'Lost' &&
          point.scoreAction.pointDetail === 'Block error' &&
          (!this.isPlayerFiltered || hasSelected(point))
      ).length;

      return {
        killBlocks,
        killBlocksSolo,
        killBlocksJoint,
        successfulBlocks,
        successfulBlocksSolo,
        successfulBlocksJoint,
        blockErrors
      };
    },
    
  },
  methods: {
    toggleCollapsed() {
      this.$emit('toggle-collapsed');
    },
    toggleFullscreen() {
      this.$emit('toggle-fullscreen');
    },
    teamSetValues(stat) {
      return this.perSetStats.map(s => s.team[stat]);
    },
    oppSetValues(stat) {
      return this.perSetStats.map(s => s.opposition[stat]);
    },
    parseStatVal(v) {
      if (v === '-' || v === null || v === undefined) return NaN;
      return typeof v === 'number' ? v : parseFloat(v);
    },
    teamWins(teamVal, oppVal, higherBetter = true) {
      if (this.showSetBreakdown) return false;
      const t = this.parseStatVal(teamVal);
      const o = this.parseStatVal(oppVal);
      if (isNaN(t) || isNaN(o)) return false;
      return higherBetter ? t > o : t < o;
    },
    oppWins(teamVal, oppVal, higherBetter = true) {
      if (this.showSetBreakdown) return false;
      const t = this.parseStatVal(teamVal);
      const o = this.parseStatVal(oppVal);
      if (isNaN(t) || isNaN(o)) return false;
      return higherBetter ? o > t : o < t;
    },
    setStatsFromPoints(points, set, playerNumber = null) {
      const isWon = p => p.scoreAction.outcome === 'Won';
      const isLost = p => p.scoreAction.outcome === 'Lost';
      const hasPlayer = p => {
        if (!playerNumber) return true;
        const primary = p.scoreAction?.player?.number;
        const secondary = p.scoreAction?.secondPlayer?.number;
        return primary === playerNumber || secondary === playerNumber;
      };
      const count = pred => points.filter(pred).length;

      const teamKills = count(p => isWon(p) && p.scoreAction.pointDetail === 'Kill' && hasPlayer(p));
      const teamAces = count(p => isWon(p) && p.scoreAction.pointDetail === 'Ace' && hasPlayer(p));
      const teamBlocks = count(p => isWon(p) && ['Kill block (solo)', 'Kill block (assist)'].includes(p.scoreAction.pointDetail) && hasPlayer(p));
      const teamAttackErrors = count(p => isLost(p) && ['Attack error', 'Opp. kill block'].includes(p.scoreAction.pointDetail) && hasPlayer(p));
      const teamBlockErrors = count(p => isLost(p) && p.scoreAction.pointDetail === 'Block error' && hasPlayer(p));
      const teamServeErrors = count(p => isLost(p) && p.scoreAction.pointDetail === 'Serve error' && hasPlayer(p));
      const teamPassErrors = count(p => p.startPoint === 'Receive' && p.receiveAction?.rating === 0 && (!playerNumber || p.receiveAction?.player?.number === playerNumber));
      const teamOtherErrors = count(p => isLost(p) && ['Other error', 'Rotation Error'].includes(p.scoreAction.pointDetail) && hasPlayer(p));

      const rallyAttacks = points.reduce((t, p) => {
        const bucket = p.duringRally?.attacks || {};
        if (!playerNumber) return t + Object.values(bucket).reduce((s, v) => s + v, 0);
        return t + (bucket[playerNumber] || 0);
      }, 0);
      const rallyDigs = points.reduce((t, p) => {
        const bucket = p.duringRally?.digs || {};
        if (!playerNumber) return t + Object.values(bucket).reduce((s, v) => s + v, 0);
        return t + (bucket[playerNumber] || 0);
      }, 0);
      const ntBlocks = points.reduce((t, p) => {
        if (!playerNumber) {
          const solo = Object.values(p.duringRally?.blocksSolo || {}).reduce((s, v) => s + v, 0);
          const joint = Object.values(p.duringRally?.blocksJoint || {}).reduce((s, v) => s + v, 0) * 0.5;
          return t + solo + joint;
        }
        return t + (p.duringRally?.blocksSolo?.[playerNumber] || 0) + (p.duringRally?.blocksJoint?.[playerNumber] || 0) * 0.5;
      }, 0);

      const servePoints = points.filter(p => p.startPoint === 'Serve' && !p.serveAction?.call && (!playerNumber || p.serveAction?.player?.number === playerNumber));
      const ts = servePoints.length;
      const sA = servePoints.filter(p => p.serveAction?.rating === 4 || p.scoreAction.pointDetail === 'Ace').length;
      const s3 = servePoints.filter(p => p.serveAction?.rating === 3).length;
      const s2 = servePoints.filter(p => p.serveAction?.rating === 2).length;
      const s1 = servePoints.filter(p => p.serveAction?.rating === 1).length;
      const sPow = servePoints.filter(p => isWon(p)).length;
      const serveRating = ts ? ((sA * 4 + s3 * 3 + s2 * 2 + s1 + sPow * 0.5) / ts).toFixed(2) : '-';

      const receivePoints = points.filter(p => p.startPoint === 'Receive' && (!playerNumber || p.receiveAction?.player?.number === playerNumber));
      const pa = receivePoints.filter(p => p.receiveAction?.rating !== null).length;
      const r3 = receivePoints.filter(p => p.receiveAction?.rating === 3).length;
      const r2 = receivePoints.filter(p => p.receiveAction?.rating === 2).length;
      const r1 = receivePoints.filter(p => p.receiveAction?.rating === 1).length;
      const passRating = pa ? ((r3 * 3 + r2 * 2 + r1) / pa).toFixed(2) : '-';

      const r0 = receivePoints.filter(p => p.receiveAction?.rating === 0).length;
      const oppPointsOnServe = receivePoints.filter(p => isLost(p)).length;
      const oppTotalServes = receivePoints.length;
      const oppServeRating = oppTotalServes
        ? ((r0 * 4 + r1 * 3 + r2 * 2 + r3 + oppPointsOnServe * 0.5) / oppTotalServes).toFixed(2)
        : '-';

      const s0 = servePoints.filter(p => p.serveAction?.rating === 0).length;
      const oppPassAttempts = Math.max(ts - s0, 0);
      const oppPassRating = oppPassAttempts
        ? ((s3 * 1 + s2 * 2 + s1 * 3) / oppPassAttempts).toFixed(2)
        : '-';

      const attackAttempts = teamKills + teamAttackErrors + rallyAttacks;
      const attackEff = attackAttempts ? `${(((teamKills - teamAttackErrors) / attackAttempts) * 100).toFixed(1)}%` : '-';

      const oppKills = count(p => isLost(p) && p.scoreAction.pointDetail === 'Opp. kill');
      const oppAces = count(p => isLost(p) && p.scoreAction.pointDetail === 'Opp. ace');
      const oppBlocks = count(p => isLost(p) && p.scoreAction.pointDetail === 'Opp. kill block');
      const oppAttackErrors = count(p => isWon(p) && ['Opp. attack error', 'Kill block (solo)', 'Kill block (assist)'].includes(p.scoreAction.pointDetail));
      const oppBlockErrors = count(p => isWon(p) && p.scoreAction.pointDetail === 'Opp. block error');
      const oppServeErrors = count(p => isWon(p) && p.scoreAction.pointDetail === 'Opp. serve error');
      const oppOtherErrors = count(p => isWon(p) && ['Opp. other error', 'Opposition Rotation Error'].includes(p.scoreAction.pointDetail));
      const oppAttempts = oppKills + oppAttackErrors + rallyDigs;
      const oppAttackEff = oppAttempts ? `${(((oppKills - oppAttackErrors) / oppAttempts) * 100).toFixed(1)}%` : '-';

      const tw = teamKills + teamBlocks + teamAces;
      const tl = teamAttackErrors + teamBlockErrors + teamPassErrors + teamServeErrors + teamOtherErrors;
      const ow = oppKills + oppBlocks + oppAces;
      const ol = oppAttackErrors + oppBlockErrors + oppServeErrors + oppOtherErrors;

      return {
        team: {
          score: set?.score?.won ?? '-',
          pointsWon: tw,
          pointsLost: tl,
          pointDiff: tw - tl,
          kills: teamKills,
          attackErrors: teamAttackErrors,
          attackEfficiency: attackEff,
          blocks: teamBlocks,
          blockErrors: teamBlockErrors,
          aces: teamAces,
          serveErrors: teamServeErrors,
          serveRating,
          passRating,
          digs: rallyDigs,
          otherErrors: teamOtherErrors,
          errorsTotal: teamAttackErrors + teamBlockErrors + teamServeErrors + teamOtherErrors,
          totalServes: ts,
          serveThrees: s3,
          serveTwos: s2,
          serveOnes: s1,
          pointsOnServe: sPow,
          passThrees: r3,
          passTwos: r2,
          passOnes: r1,
          passErrors: r0,
          passAttempts: pa,
          totalReceives: receivePoints.length,
          oppServeErrors: receivePoints.filter(p => p.scoreAction.pointDetail === 'Opp. serve error').length,
          attackAttempts: attackAttempts,
          successfulBlocks: teamBlocks + ntBlocks,
          acesPercent: ts ? `${((teamAces / ts) * 100).toFixed(1)}%` : '-',
          serveThreesPercent: ts ? `${((s3 / ts) * 100).toFixed(1)}%` : '-',
          serveTwosPercent: ts ? `${((s2 / ts) * 100).toFixed(1)}%` : '-',
          serveOnesPercent: ts ? `${((s1 / ts) * 100).toFixed(1)}%` : '-',
          serveErrorsPercent: ts ? `${((s0 / ts) * 100).toFixed(1)}%` : '-',
          pointsOnServePercent: ts ? `${((sPow / ts) * 100).toFixed(1)}%` : '-',
          passThreesPercent: pa ? `${((r3 / pa) * 100).toFixed(1)}%` : '-',
          passTwosPercent: pa ? `${((r2 / pa) * 100).toFixed(1)}%` : '-',
          passOnesPercent: pa ? `${((r1 / pa) * 100).toFixed(1)}%` : '-',
          passErrorsPercent: pa ? `${((r0 / pa) * 100).toFixed(1)}%` : '-',
          killsPercent: attackAttempts ? `${((teamKills / attackAttempts) * 100).toFixed(1)}%` : '-',
          attackErrorsPercent: attackAttempts ? `${((teamAttackErrors / attackAttempts) * 100).toFixed(1)}%` : '-',
          blocksPercent: (teamBlocks + teamBlockErrors) ? `${((teamBlocks / (teamBlocks + teamBlockErrors)) * 100).toFixed(1)}%` : '-',
          blockErrorsPercent: (teamBlocks + teamBlockErrors) ? `${((teamBlockErrors / (teamBlocks + teamBlockErrors)) * 100).toFixed(1)}%` : '-'
        },
        opposition: {
          score: set?.score?.lost ?? '-',
          pointsWon: ow,
          pointsLost: ol,
          pointDiff: ow - ol,
          kills: oppKills,
          attackErrors: oppAttackErrors,
          attackEfficiency: oppAttackEff,
          blocks: oppBlocks,
          blockErrors: oppBlockErrors,
          aces: oppAces,
          serveErrors: oppServeErrors,
          serveRating: oppServeRating,
          passRating: oppPassRating,
          digs: rallyAttacks + ntBlocks,
          otherErrors: oppOtherErrors,
          errorsTotal: oppAttackErrors + oppBlockErrors + oppServeErrors + oppOtherErrors,
          killsPercent: oppAttempts ? `${((oppKills / oppAttempts) * 100).toFixed(1)}%` : '-',
          attackErrorsPercent: oppAttempts ? `${((oppAttackErrors / oppAttempts) * 100).toFixed(1)}%` : '-',
          acesPercent: oppTotalServes ? `${((oppAces / oppTotalServes) * 100).toFixed(1)}%` : '-',
          serveErrorsPercent: oppTotalServes ? `${((oppServeErrors / oppTotalServes) * 100).toFixed(1)}%` : '-',
          blocksPercent: (oppBlocks + oppBlockErrors) ? `${((oppBlocks / (oppBlocks + oppBlockErrors)) * 100).toFixed(1)}%` : '-',
          blockErrorsPercent: (oppBlocks + oppBlockErrors) ? `${((oppBlockErrors / (oppBlocks + oppBlockErrors)) * 100).toFixed(1)}%` : '-'
        }
      };
    }
  }
};
</script>

<style scoped lang="scss">
.stats-display {
  position: fixed;
  bottom: 0px;
  left: 0px;
  right: 0px;

  &.inline {
    position: static;
    margin: 0;
    border-radius: 0;
    max-height: none;
    background: transparent;
    backdrop-filter: none;
    z-index: auto;
    overflow-y: visible;
  }
  background: var(--grey-5);
  backdrop-filter: blur(10px);
  margin: 10px;
  border-radius: 12px;
  z-index: 100;
  max-height: 200px;
  overflow-y: auto;
  transition: max-height 0.2s ease, right 0.3s ease;

  &.sidebar-open {
    right: 380px;
  }

  &.sidebar-collapsed {
    right: 40px;
  }

  &.collapsed {
    overflow: hidden;
  }

  &.fullscreen {
    top: 0px;
    max-height: none;
    z-index: 1100;
  }

  .stats-header {
    padding: 8px;
    margin: 0;
    display: flex;
    gap: 6px;
    align-items: flex-start;
    justify-content: space-between;

    .stats-tabs {
      display: flex;
      gap: 6px;
      flex-wrap: nowrap;
    }

    .header-actions {
      display: flex;
      gap: 8px;
    }

    .tab-btn {
      background: var(--grey-50);
      padding: 4px 10px;

      &:hover {
        background: var(--grey-100);
      }

      &:active {
        background: var(--grey-200);
      }

      &.active {
        background: var(--grey-100);
      }
    }

    .collapse-btn,
    .fullscreen-btn {
      background: var(--grey-50);
      padding: 4px 10px;

      &:hover {
        background: var(--grey-100);
      }

      &:active {
        background: var(--grey-200);
      }
    }
  }

  .stats-content {
    padding: 12px 0 0;
    display: flex;
    flex-direction: column;
    gap: 6px;

    .stat-grid {
      display: grid;
      gap: 6px;
      grid-template-columns: repeat(auto-fill, minmax(170px, max-content));
    }

    .stat-card {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 6px;
      border-radius: 8px;
      background: var(--grey-25);
      font-size: 13px;
      min-height: 82px;
      justify-content: flex-start;
      box-sizing: border-box;

      .stat-main {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 4px;

        &.zero-non-participant .stat-value,
        &.zero-non-participant .stat-percent {
          opacity: 0.2;
        }
      }

      .stat-sets {
        display: flex;
        align-items: stretch;
        width: 100%;
        padding: 4px 0 0;
        margin-top: auto;
        gap: 2px;

        .stat-set-col {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0px;
          text-align: center;
          background: white;
          border-radius: 4px;
          padding: 3px 6px;
          width: 40px;
          box-sizing: border-box;
          height: 30px;
          justify-content: center;
          align-items: center;

          &.non-participant {
            opacity: 0.2;
          }

          > span {
            font-size: 10px;
            font-weight: 800;
            color: var(--grey-800);
          }

          > span.pct {
            font-size: 9px;
            font-weight: 500;
            color: var(--grey-400);
          }
        }
      }
    }

    .stat-label {
      color: var(--grey-300);
    }

    .stat-value {
      font-weight: 700;
      color: var(--grey-800);
      font-size: 18px;
    }

    .stat-percent {
      color: var(--grey-300);
    }

    .stat-subtext {
      font-size: 12px;
      color: var(--grey-300);
      font-weight: 500;
    }

    .match-stats-grid {
      display: grid;
      grid-template-columns: 120px 160px 120px;
      gap: 6px;
      align-items: center;
      font-size: 13px;
      width: fit-content;
      margin: 0 auto;
      justify-items: center;

      &.breakdown {
        grid-template-columns: 1fr 160px 1fr;

        .match-stats-value {
          width: fit-content;
        }

        .msv-sets {
          width: auto;

          > span:not(.set-div) {
            flex: none;
            width: 40px;
            height: 26px;
            display: flex;
            justify-content: center;
            align-items: center;
            box-sizing: border-box;

            &.has-pct {
              height: auto;
              flex-direction: column;
              align-items: center;
              gap: 0;
              padding: 3px 0;
            }
          }
        }
      }
    }

    .match-stats-header {
      font-weight: 700;
      color: var(--grey-800);
      text-align: center;
      white-space: nowrap;
      padding: 10px;
    }

    .match-stats-group {
      display: contents;
    }

    .match-stats-spacer {
      grid-column: 1 / -1;
      height: 8px;
    }

    .match-stats-value {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      text-align: center;
      font-weight: 700;
      color: var(--grey-800);
      background: var(--grey-25);
      width: 80px;
      box-sizing: border-box;
      border-radius: 8px;
      padding: 6px;
    }

    .msv-total {
      font-size: 1.3em;
      line-height: 1.5;
      font-weight: 700;
      color: var(--grey-1000);
    }

    .msv-pct {
      font-size: 10px;
      font-weight: 500;
      color: var(--grey-400);
      line-height: 1.2;
    }

    .match-stats-value.msv-win {
      .msv-total {
        background: var(--green-200);
        width: 100%;
        border-radius: 6px;
      }

      .msv-sets {
        color: var(--grey-400);
        border-top-color: var(--grey-600);
      }
    }

    .msv-sets {
      display: flex;
      gap: 2px;
      align-items: center;
      width: 100%;
      font-size: 10.5px;
      font-weight: 600;
      color: var(--grey-400);

      > span:not(.set-div) {
        flex: 1;
        text-align: center;
        flex: none;
        width: 30px;
        background: var(--grey-5);
        border-radius: 4px;
        padding: 4px 0;

        &.has-pct {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0;
          padding: 3px 0;

          > span {
            line-height: 1.3;
          }

          > .sub {
            font-size: 8.5px;
            font-weight: 500;
            color: var(--grey-300);
          }
        }
      }

      > .set-div {
        display: none;
      }
    }

    .match-stats-label {
      color: var(--grey-800);
      font-weight: 600;
    }
  }
}

.stats-display.summary-mode {
  position: static;
  max-height: none;
  overflow: visible;
  border-top: none;
  background: transparent;
  backdrop-filter: none;
  padding: 0;

  .stats-content {
    padding: 0;
  }
}
</style>